import {useContext, useState, useCallback} from 'react'
import {Button, Dimmer, Form, Header, Input, Loader, Modal, Segment} from 'semantic-ui-react'
import {FileReader} from '../file-reader'
import {DataSourceID, DataSourceTemplates, DataSourcesContext} from '../..'
import {MessageContext} from '@/features/errors'
import _ from 'lodash'
import {usePostHog} from 'posthog-js/react'

export interface IDataSourceOverrideDialogProps {
  // children: React.ReactNode,
  onClose?: () => void
}

export function DataSourceOverrideDialog({onClose}: IDataSourceOverrideDialogProps) {
  const posthog = usePostHog()

  const {logError} = useContext(MessageContext)

  const {
    isBusy,
    isDialogOpen,
    sourceData,
    sourceConfigs,
    // mergedItems,
    activeCatalogName,
    replaceSources,
  } = useContext(DataSourcesContext)

  const [apiCatalogNameOverrideForm, setApiCatalogNameOverrideForm] = useState(activeCatalogName)

  const handleAddCatalogSource = useCallback(() => {
    try {
      replaceSources([DataSourceTemplates.catalog({catalogName: apiCatalogNameOverrideForm})])
      posthog.capture('add_catalog_source', {
        catalogName: apiCatalogNameOverrideForm,
      })
    } catch (error) {
      logError(error instanceof Error ? error.message : 'unknown error', error)
    }
  }, [apiCatalogNameOverrideForm, replaceSources, logError])

  const handleAddFileSource = useCallback(
    (files: File[]) => {
      try {
        replaceSources(files.map((f) => DataSourceTemplates.localFile({file: f})))
        posthog.capture('add_file_source', {
          fileInfo: files.map((f) => ({
            name: f.name,
            size: f.size,
            type: f.type,
          })),
        })
      } catch (error) {
        logError(error instanceof Error ? error.message : 'unknown error', error)
      }
    },
    [replaceSources, posthog, logError]
  )

  const getCounts = useCallback(
    (sourceID: DataSourceID, key: string) => {
      if (!key || !sourceData?.[sourceID]?.[key]) return {total: 0, counts: {}}
      const total = _.chain(sourceData).get(sourceID).get(key).keys().value().length

      const counts: Record<string, number> = {}

      _.values(sourceData[sourceID][key]).forEach((item) => {
        if (!item.type) return
        counts[item.type] = (counts[item.type] || 0) + 1
      })

      return {total, counts}
    },
    [sourceData]
  )

  return (
    <Modal
      className='data-sources-overrides-dialog'
      open={isDialogOpen}
      onClose={onClose}
    >
      <Modal.Header>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
          }}
        >
          <span>data sources</span>
          <Button
            compact
            size='tiny'
            icon='close'
            color='red'
            onClick={onClose}
          />
        </div>
      </Modal.Header>
      <Modal.Content>
        <div style={{position: 'relative', marginBottom: '-1rem'}}>
          <Segment inverted>
            <Header
              as='h4'
              icon='cloud download'
              content='Read from API'
            />
            <div className='api-form'>
              <Form>
                <Form.Group>
                  <Form.Field
                    control={Input}
                    label='Catalog name'
                    width={13}
                    value={apiCatalogNameOverrideForm || ''}
                    onChange={(_e: unknown, {value}: {value: string}) =>
                      setApiCatalogNameOverrideForm(value)
                    }
                  />
                  <Form.Field
                    control={Button.Group}
                    label='&nbsp;'
                    width={3}
                    fluid
                  >
                    <Button
                      icon='download'
                      content='Load'
                      color='blue'
                      onClick={handleAddCatalogSource}
                    />
                  </Form.Field>
                </Form.Group>
              </Form>
            </div>
          </Segment>

          <Segment inverted>
            <Header
              as='h4'
              icon='upload'
              content='Local data'
            />
            <div style={{display: 'flex', gap: '0.5em'}}>
              <div style={{position: 'relative'}}>
                <FileReader onFilesAccepted={handleAddFileSource} />
              </div>
            </div>
          </Segment>

          {sourceConfigs.length > 0 && (
            <Segment inverted>
              <Header
                as='h5'
                content='sources'
              />
              <ul>
                {sourceConfigs.map(({sourceID, key}) => {
                  const {total, counts} = getCounts(sourceID, key)
                  return (
                    <li key={key}>
                      {sourceID}: {key} ({total} items{Object.keys(counts).length > 0 ? ' - ' : ''}
                      {Object.entries(counts)
                        .map(([type, count]) => `${type}: ${count}`)
                        .join(', ')}
                      )
                    </li>
                  )
                })}
              </ul>
            </Segment>
          )}
          <Dimmer active={isBusy}>
            <Loader />
          </Dimmer>
        </div>
      </Modal.Content>
    </Modal>
  )
}
